/**
 * Filter Engine
 * Parses and applies filter expressions for DROPDOWN_DB fields.
 *
 * Filter Syntax:
 * - Field reference: {{object[index].field}} e.g., {{account[0].level1}}
 * - Operators: =, !=, >, <, >=, <=
 * - Logical: AND, OR
 *
 * Examples:
 * - "risk={{account[0].level1}}"
 * - "risk={{account[0].level1}} AND type={{account[0].level2}}"
 * - "status='active'"
 */

// Regex to match field references: {{object[index].field}}
const FIELD_REF_REGEX = /\{\{([^}]+)\}\}/g;

// Regex to parse a single field reference: object[index].field
const FIELD_PATH_REGEX = /^(\w+)\[(\d+)\]\.(\w+)$/;

/**
 * Parse a field reference path and get the value from form data
 * @param {string} path - Field path like "account[0].level1"
 * @param {Object} formData - Form data object
 * @returns {*} - The value from form data or undefined
 */
export function resolveFieldRef(path, formData) {
  if (!path || !formData) return undefined;

  const match = path.match(FIELD_PATH_REGEX);
  if (!match) {
    // Try simple path without array index (e.g., "level1")
    // This handles legacy format or simplified references
    return undefined;
  }

  const [, objectName, indexStr, fieldName] = match;
  const index = parseInt(indexStr, 10);

  // Get object array from form data
  const objectArray = formData[objectName];
  if (!Array.isArray(objectArray) || objectArray.length <= index) {
    return undefined;
  }

  // Get field value from object
  const obj = objectArray[index];
  return obj ? obj[fieldName] : undefined;
}

/**
 * Replace all field references in expression with actual values
 * @param {string} expression - Filter expression with {{...}} references
 * @param {Object} formData - Form data object
 * @returns {string} - Expression with values substituted
 */
export function replaceFieldRefs(expression, formData) {
  if (!expression) return '';

  return expression.replace(FIELD_REF_REGEX, (match, path) => {
    const value = resolveFieldRef(path, formData);
    if (value === undefined || value === null || value === '') {
      return ''; // Return empty string for null/undefined values
    }
    return String(value);
  });
}

/**
 * Parse a single condition string (e.g., "column=value" or "column='value'")
 * @param {string} condition - Single condition string
 * @returns {Object|null} - { column, operator, value } or null if invalid
 */
export function parseCondition(condition) {
  if (!condition || typeof condition !== 'string') return null;

  const trimmed = condition.trim();
  if (!trimmed) return null;

  // Match operators: >=, <=, !=, =, >, <
  const operatorMatch = trimmed.match(/^([^!=<>]+?)\s*(>=|<=|!=|=|>|<)\s*(.+)$/);
  if (!operatorMatch) return null;

  let [, column, operator, value] = operatorMatch;
  column = column.trim();
  value = value.trim();

  // Remove quotes from value if present
  if ((value.startsWith("'") && value.endsWith("'")) ||
      (value.startsWith('"') && value.endsWith('"'))) {
    value = value.slice(1, -1);
  }

  return { column, operator, value };
}

/**
 * Evaluate a single condition against a data row
 * @param {Object} row - Data row to check
 * @param {Object} condition - Parsed condition { column, operator, value }
 * @returns {boolean}
 */
export function evaluateCondition(row, condition) {
  if (!row || !condition) return false;

  const { column, operator, value } = condition;
  const rowValue = row[column];

  // Handle null/undefined values
  if (rowValue === undefined || rowValue === null) {
    // Empty filter value matches null/undefined row values
    if (value === '' || value === undefined || value === null) {
      return true;
    }
    return false;
  }

  // Convert to strings for comparison
  const rowStr = String(rowValue);
  const valueStr = String(value);

  switch (operator) {
    case '=':
      return rowStr === valueStr;
    case '!=':
      return rowStr !== valueStr;
    case '>':
      return parseFloat(rowValue) > parseFloat(value);
    case '<':
      return parseFloat(rowValue) < parseFloat(value);
    case '>=':
      return parseFloat(rowValue) >= parseFloat(value);
    case '<=':
      return parseFloat(rowValue) <= parseFloat(value);
    default:
      return false;
  }
}

/**
 * Parse filter expression into structured conditions
 * @param {string} expression - Filter expression (already with values substituted)
 * @returns {Object} - { type: 'AND'|'OR'|'SINGLE', conditions: Array }
 */
export function parseFilterExpression(expression) {
  if (!expression || typeof expression !== 'string') {
    return { type: 'NONE', conditions: [] };
  }

  const trimmed = expression.trim();
  if (!trimmed) {
    return { type: 'NONE', conditions: [] };
  }

  // Check for AND (case insensitive)
  if (/\s+AND\s+/i.test(trimmed)) {
    const parts = trimmed.split(/\s+AND\s+/i);
    const conditions = parts.map(part => parseCondition(part)).filter(Boolean);
    return { type: 'AND', conditions };
  }

  // Check for OR (case insensitive)
  if (/\s+OR\s+/i.test(trimmed)) {
    const parts = trimmed.split(/\s+OR\s+/i);
    const conditions = parts.map(part => parseCondition(part)).filter(Boolean);
    return { type: 'OR', conditions };
  }

  // Single condition
  const condition = parseCondition(trimmed);
  if (condition) {
    return { type: 'SINGLE', conditions: [condition] };
  }

  return { type: 'NONE', conditions: [] };
}

/**
 * Apply filter to data array
 * @param {Array} data - Data array to filter
 * @param {string} filterExpression - Filter expression with {{...}} references
 * @param {Object} formData - Form data object for resolving references
 * @returns {Array} - Filtered data array
 */
export function filterData(data, filterExpression, formData) {
  // Return all data if no filter
  if (!filterExpression || typeof filterExpression !== 'string') {
    return data || [];
  }

  if (!Array.isArray(data)) {
    return [];
  }

  // Replace field references with actual values
  const resolvedExpression = replaceFieldRefs(filterExpression, formData);

  // Check if any reference wasn't resolved (empty value)
  // If parent field is empty, return empty array
  if (resolvedExpression.includes('=') && /=\s*($|\s+AND|\s+OR)/i.test(resolvedExpression)) {
    return [];
  }

  // Parse the expression
  const parsed = parseFilterExpression(resolvedExpression);

  if (parsed.type === 'NONE' || parsed.conditions.length === 0) {
    return data;
  }

  // Filter the data
  return data.filter(row => {
    if (parsed.type === 'AND') {
      // All conditions must be true
      return parsed.conditions.every(condition => evaluateCondition(row, condition));
    } else if (parsed.type === 'OR') {
      // At least one condition must be true
      return parsed.conditions.some(condition => evaluateCondition(row, condition));
    } else {
      // Single condition
      return evaluateCondition(row, parsed.conditions[0]);
    }
  });
}

/**
 * Extract parent field references from filter expression
 * Used for dependency tracking
 * @param {string} filterExpression - Filter expression with {{...}} references
 * @returns {Array<Object>} - Array of { object, index, field } for each reference
 */
export function extractParentFields(filterExpression) {
  if (!filterExpression || typeof filterExpression !== 'string') {
    return [];
  }

  const matches = [];
  let match;

  // Reset regex
  FIELD_REF_REGEX.lastIndex = 0;

  while ((match = FIELD_REF_REGEX.exec(filterExpression)) !== null) {
    const path = match[1];
    const pathMatch = path.match(FIELD_PATH_REGEX);

    if (pathMatch) {
      const [, objectName, indexStr, fieldName] = pathMatch;
      matches.push({
        object: objectName,
        index: parseInt(indexStr, 10),
        field: fieldName,
        fullPath: path,
      });
    }
  }

  return matches;
}

/**
 * Check if parent fields have values (for HideIfParentEmpty)
 * @param {string} filterExpression - Filter expression
 * @param {Object} formData - Form data object
 * @returns {boolean} - True if all parent fields have values
 */
export function hasParentValues(filterExpression, formData) {
  const parentFields = extractParentFields(filterExpression);

  if (parentFields.length === 0) {
    return true; // No dependencies, always show
  }

  return parentFields.every(({ object, index, field }) => {
    const objectArray = formData[object];
    if (!Array.isArray(objectArray) || objectArray.length <= index) {
      return false;
    }
    const value = objectArray[index]?.[field];
    return value !== undefined && value !== null && value !== '';
  });
}

// Default export as object
const FilterEngine = {
  resolveFieldRef,
  replaceFieldRefs,
  parseCondition,
  evaluateCondition,
  parseFilterExpression,
  filterData,
  extractParentFields,
  hasParentValues,
};

export default FilterEngine;
